<template>
    <div class="c-table">
        <form @submit.prevent="applyFilters" novalidate>
            <div class="row collapse">
                <div class="columns">
                    <c-search v-if="search" @input="applySearch" :placeholder="placeholder"></c-search>

                    <template v-if="filters.length > 0">
                        <div class="c-table__filters">
                            <div class="row">
                                <component
                                    v-for="(filter, index) in filters"
                                    :is="getFilterClass(filter)"
                                    :filter="filter"
                                    :key="index"
                                    :selected="filterSelected(filter)"
                                    @change="setFilter">
                                </component>
                            </div>
                        </div>
                        <div class="c-table__tags">
                            <c-tag
                                v-for="(filter, index) in applied.filters"
                                :filter="filter.filter"
                                :key="index"
                                @remove="removeFilter">
                                {{getOption(filter)}}
                            </c-tag>
                            <a href="#" class="fs-xsmall color-dark-gray" @click.prevent="clearFilters" v-if="applied.filters.length">Filters wissen</a>
                        </div>
                    </template>
                </div>
                <div class="columns shrink">
                    <button
                        v-if="filters.length > 0 || search"
                        type="submit"
                        class="c-btn c-btn--secondary c-btn--hollow margin-l-16 c-table__filter-btn">
                        <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><defs><path d="m10.877 17.457 2.026 1.533v-4.553c0-.166.042-.329.12-.475L17.323 6H6.643l4.122 7.978c.074.142.112.3.112.459v3.02ZM13.903 22a.9938.9938 0 0 1-.603-.203l-4.026-3.045a.9993.9993 0 0 1-.397-.797v-3.274L4.112 5.459a.9963.9963 0 0 1 .034-.979C4.327 4.182 4.651 4 5 4h14c.352 0 .678.185.859.488.18.302.188.677.021.987l-4.977 9.215V21a.999.999 0 0 1-.554.895c-.141.07-.294.105-.446.105Z" id="filter"/></defs><use fill="currentColor" fill-rule="nonzero" xlink:href="#filter"/></svg>
                        <span>{{ filters.length> 0 ? 'Filteren' : 'Zoeken' }}</span>
                    </button>
                </div>
            </div>
        </form>
        <div class="c-table c-table--responsive">
            <table class="c-table__element">
                <thead>
                <tr>
                    <th v-for="column in columns">
                        <a href="#"
                           class="c-table__column"
                           :class="[
                                    {'c-table__column--sorted-asc': column.name == sort.column && sort.direction == 'asc'},
                                    {'c-table__column--sorted-desc': column.name == sort.column && sort.direction == 'desc'},
                                    {'c-table__column--sortable': column.sortable == true }
                                ]"
                           @click.prevent="sortColumn(column)"
                        >{{column.label}}</a>
                    </th>
                </tr>
                </thead>
                <tbody>
                <tr v-if="loading">
                    <td :colspan="columns.length">
                        <c-loading-container :loading="true"></c-loading-container>
                    </td>
                </tr>
                <tr v-else-if="rows.length == 0">
                    <td :colspan="columns.length" class="text-center">Er is geen data gevonden</td>
                </tr>
                <tr v-else v-for="row in rows" :class="{'text-bold': row['highlight_row'] === true}">
                    <td v-for="(column, index) in columns">
                        <component :is="column.component || 'CColumnText'" v-bind="column.props" :extra-values="getExtraValues(column.extraValues, row)" :value="row[column.name]"></component>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>
        <c-pagination
            v-if="!loading && page.total > 1"
            :current="page.current"
            :pages="page.total"
            @select="setPage"></c-pagination>
    </div>
</template>

<script>
import CFilterSelect from '@components/table/filter/Select';
import CFilterDate from '@components/table/filter/Date';

import CPagination from '@components/table/Pagination';
import CSearch from '@components/table/Search';
import CTag from '@components/table/Tag';

import CColumnButton from '@components/table/column/Button';
import CColumnCustomButton from '@components/table/column/CustomButton';
import CColumnDatetime from '@components/table/column/Datetime';
import CColumnDate from '@components/table/column/Date';
import CColumnTime from '@components/table/column/Time';
import CColumnMessages from '@components/table/column/Messages';
import CColumnStatus from '@components/table/column/Status';
import CColumnText from '@components/table/column/Text';
import CColumnUrgency from '@components/table/column/Urgency';
import CColumnBoolean from '@components/table/column/Boolean';
import CColumnCents from '@components/table/column/Cents';

import CLoadingContainer from '@components/loader/Container';

export default {
    components: {
        CFilterSelect,
        CFilterDate,
        CPagination,
        CSearch,
        CTag,
        CColumnButton,
        CColumnCustomButton,
        CColumnDatetime,
        CColumnDate,
        CColumnTime,
        CColumnMessages,
        CColumnStatus,
        CColumnText,
        CColumnUrgency,
        CLoadingContainer,
        CColumnBoolean,
        CColumnCents
    },
    props: {
        search: {
            type:Boolean,
            default: true
        },
        columns: {
            type: Array,
            required: true
        },
        filters: {
            type: Array,
            default: () => []
        },
        endpoint: {
            type: String,
            required: true
        },
        length: {
            type: Number,
            default: 25
        },
        placeholder: {
            type: String,
            default: 'Zoeken op aanvraag, nummer, status...'
        }
    },
    data() {
        return {
            selection: {
                filters: [],
                search: ''
            },
            applied: {
                filters: [],
                search: ''
            },
            page: {
                current: 1,
                total: 0
            },
            sort: {
                direction: 'asc',
                column: null
            },
            loading: true,
            rows: []
        };
    },
    methods: {

        /**
         * Search methods
         */
        applySearch(value) {
            this.selection.search = value;
        },

        /**
         * Filter methods
         */
        getFilterClass(filter) {
            switch (filter.type) {
                case 'date':
                    return 'c-filter-date';
                default:
                    return 'c-filter-select';
            }
        },

        filterSelected(filter) {
            let applied = this.applied.filters.find( f => f.filter.name == filter.name );
            return ( applied ) ? applied.value : false;
        },

        setFilter( {filter, value} ){

            if ( value === '' ) {
                this.removeFilter(filter, false);
            }
            else {
                let index = this.selection.filters.findIndex( f => f.filter.name == filter.name );

                if ( index >= 0  ) {
                    this.selection.filters[index] = { filter, value };
                }
                else {
                    this.selection.filters.push({ filter, value });
                }
            }
        },

        removeFilter(filter, reload) {

            let index = this.selection.filters.findIndex( f => f.filter.name == filter.name );

            if ( index >= 0  ) {
                this.selection.filters.splice(index, 1);
            }

            if ( this.selection.filters.length == 0 ){
                this.preselect(true);
            }

            if ( reload !== false ) {

                this.applyFilters();
            }
        },

        applyFilters() {

            this.$nextTick( () => {
                this.page.current = 1;
                this.applied.filters = [ ...this.selection.filters ];
                this.applied.search = this.selection.search;
                this.load();
            });
        },

        clearFilters() {
            this.selection.filters = [];
            this.preselect(true);

            this.applyFilters();
        },

        getOption({ filter, value }) {

            if ( filter.options ) {
                let option = filter.options.find( o => o.value == value);
                return option.label || '-';
            }
            else if( filter.type == 'date') {

                if ( value[0] != '' && value[1] != '' ){
                    return this.$options.filters.prettydate(value[0])  + ' - ' + this.$options.filters.prettydate(value[1]);
                }
                else if ( value[0] != '' ){
                    return 'Van ' + this.$options.filters.prettydate(value[0]);
                }
                else if ( value[1] != '' ){
                    return 'Tot ' + this.$options.filters.prettydate(value[1]);
                }
            }

            return value;
        },

        getRequestFilters() {

            let output = [];

            this.applied.filters.forEach( ({filter, value}) => {
                output.push({ name: filter.name, value });
            });

            return output;
        },

        /**
         * Pagination methods
         */
        setPage(page) {
            this.page.current = page;
            this.load();
        },

        /**
         * Table methods
         */
        sortColumn(column) {

            if ( column.sortable ){
                if ( this.sort.column == column.name ){
                    this.sort.direction = ( this.sort.direction == 'asc' ) ? 'desc' : 'asc';
                } else {
                    this.sort.column = column.name;
                    this.sort.direction = 'asc';
                }

                this.load();
            }
        },

        load() {
            this.loading = true;

            let params = {
                page: this.page.current,
                sort: this.sort,
                length: this.length
            };

            if ( this.applied.filters.length ) {
                params.filters = this.getRequestFilters();
            }

            if ( this.applied.search ) {
                params.search = this.applied.search;
            }

            this.$emit('load', {filters: params.filters, search: this.applied.search});

            this.$xhr.get(this.endpoint, params).then( ({result}) => {

                this.rows = result.data;
                this.page.total = result.pages;

                this.loading = false;

            }).catch( (e) => {

                this.loading = false;
                this.rows = [];

                this.$store.dispatch('toasts/alert', {title: 'Foutmelding', text: 'Er kan geen data geladen worden'});

            });
        },

        preselect(selection) {

            this.filters.forEach( (filter) => {
                if ( filter.options ) {
                    let preselect = filter.options.find( o => o.preselected == true );

                    if ( preselect ) {

                        if ( selection ) {
                            this.selection.filters.push({filter, value: preselect.value});
                        }
                        else {
                            this.applied.filters.push({filter, value: preselect.value});
                        }
                    }
                }
            });
        },
        getExtraValues(columns, row) {
            if (columns && columns.constructor === Array && row && row.constructor === Object) {
                let values = {}
                for (const column of columns) {
                    if (row[column]) {
                        values[column] = row[column]
                    }
                }
                return values
            }
            return {}
        }
    },
    mounted() {

        /**
         * Sort the first column
         */
        let column = this.columns.find( c => c.sort === true || c.sort === 'asc' || c.sort === 'desc');
        this.sort.column = column ? column.name : this.columns[0].name;
        if ( typeof column !== 'undefined') {
            this.sort.direction = column.sort === 'desc' ? 'desc' : 'asc';
        }

        this.preselect();

        /**
         * Load data
         */
        this.load();
    }
}
</script>
